// ============================================================
// FILE: src/components/Hero.jsx
// ============================================================
import React, { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, X } from "lucide-react";
import { Link } from "react-router-dom";

const Hero = () => {
    const [modalOpen, setModalOpen] = useState(false);
    const [contactData, setContactData] = useState({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
    });
    const [submitted, setSubmitted] = useState(false);

    const handleInputChange = (e) => {
        setContactData({
            ...contactData,
            [e.target.name]: e.target.value,
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setSubmitted(true);

        setContactData({
            name: "",
            email: "",
            phone: "",
            subject: "",
            message: "",
        });

        setTimeout(() => {
            setSubmitted(false);
        }, 4000);
    };

    return (
        <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="relative overflow-hidden bg-[var(--color-dark)] pt-24 pb-16 sm:pt-30 sm:pb-20 lg:pt-32 lg:pb-24"
        >
            {/* =====================================================
                BACKGROUND
            ====================================================== */}
            <div className="absolute inset-0 z-0 overflow-hidden">

                {/* Base dark background */}
                <div className="absolute inset-0 bg-black" />

                {/* Background Image */}
                <div
                    className="absolute inset-0 bg-cover bg-center opacity-15"
                    style={{
                        backgroundImage:
                            "url('https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?auto=format&fit=crop&w=1920&q=80')",
                    }}
                />

                {/* 
                    Light white gradient at the top.
                    This makes the area behind the navbar/logo
                    brighter and reduces image visibility there.
                */}
                <div className="absolute inset-0 bg-gradient-to-b from-white/40 via-white/10 to-transparent" />

                {/* 
                    Dark gradient toward the bottom.
                    Keeps the hero text easy to read.
                */}
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/20 to-black/75" />

                {/* Soft cyan glow */}
                <div className="absolute -top-24 -left-16 h-72 w-72 rounded-full bg-cyan-500/15 blur-3xl hero-bg-pulse" />

                {/* Soft blue glow */}
                <div className="absolute -bottom-28 -right-16 h-80 w-80 rounded-full bg-blue-500/10 blur-3xl hero-bg-pulse" />

                {/* Decorative ring */}
                <div className="absolute inset-0 hero-gradient-ring pointer-events-none" />
            </div>

            {/* =====================================================
                HERO CONTENT
            ====================================================== */}
            <div className="container mx-auto relative z-10 px-6 lg:px-8">
                <div className="grid items-center gap-6 sm:gap-8 lg:grid-cols-[1.05fr_0.95fr] lg:gap-10">

                    {/* =================================================
                        LEFT CONTENT
                    ================================================== */}
                    <div className="max-w-3xl text-left">

                        {/* Small Label */}
                        <motion.span
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{
                                duration: 0.8,
                                delay: 0.1,
                            }}
                            className="pill border-white/20 bg-white/10 text-white"
                        >
                            Formerly Chauhan Fabrications.
                        </motion.span>

                        {/* Main Heading */}
                        <motion.h1
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{
                                duration: 0.8,
                                delay: 0.15,
                            }}
                            className="mt-6 text-4xl font-bold leading-[0.95] text-[var(--color-surface)] sm:text-5xl md:text-6xl lg:text-7xl"
                        >
                            Building Strength,
                            <br />

                            <span className="gradient-text text-white/90">
                                Creating Spaces
                            </span>
                        </motion.h1>

                        {/* Description */}
                        <motion.p
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{
                                duration: 0.8,
                                delay: 0.25,
                            }}
                            className="mt-5 max-w-2xl text-white leading-relaxed text-[var(--color-surface)]/85 sm:mt-6 sm:text-lg lg:text-xl"
                        >
                            Premium steel fabrication and infrastructure
                            solutions for industrial, commercial, and
                            residential projects - crafted with precision,
                            integrity, and a modern edge.
                        </motion.p>

                        {/* Buttons */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{
                                duration: 0.8,
                                delay: 0.4,
                            }}
                            className="mt-7 flex flex-wrap gap-4 sm:mt-8"
                        >
                            {/* Get Quote */}
                            <button
                                type="button"
                                onClick={() => setModalOpen(true)}
                                className="btn-primary flex items-center justify-center gap-2"
                            >
                                Get a Quote
                                <ArrowRight size={18} />
                            </button>

                            {/* Services */}
                            <Link
                                to="/services"
                                className="btn-outline border-[var(--color-surface)] text-[var(--color-surface)] hover:bg-[var(--color-surface)] hover:text-[var(--color-dark)]"
                            >
                                Our Services
                            </Link>
                        </motion.div>
                    </div>

                    {/* =================================================
                        RIGHT INFORMATION CARD
                    ================================================== */}
                    <motion.div
                        initial={{ opacity: 0, x: 24 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{
                            duration: 0.8,
                            delay: 0.3,
                        }}
                        className="hidden rounded-[32px] border border-white/10 bg-white/10 p-4 shadow-[0_24px_80px_rgba(0,0,0,0.2)] backdrop-blur-xl sm:block"
                    >
                        <div className="rounded-[28px] border border-white/10 bg-[var(--color-surface)] p-5 sm:p-6">

                            {/* Card Header */}
                            <div className="flex items-center justify-between">
                                <div>
                                    <h3 className="mt-2 text-xl font-semibold text-[var(--color-dark)]">
                                        Built for long-term performance
                                    </h3>
                                </div>

                                <div className="rounded-2xl bg-[var(--color-accent)]/10 p-3 text-[var(--color-accent)]">
                                    ⚙️
                                </div>
                            </div>

                            {/* Stats */}
                            <div className="mt-5 grid gap-3 sm:grid-cols-2">

                                {/* Years */}
                                <div className="rounded-2xl border border-[var(--color-border)]/70 bg-white p-4">
                                    <p className="text-2xl font-semibold text-[var(--color-dark)]">
                                        40+
                                    </p>

                                    <p className="mt-1 text-sm text-slate-600">
                                        Years of expertise
                                    </p>
                                </div>

                                {/* Projects */}
                                <div className="rounded-2xl border border-[var(--color-border)]/70 bg-white p-4">
                                    <p className="text-2xl font-semibold text-[var(--color-dark)]">
                                        10K+
                                    </p>

                                    <p className="mt-1 text-sm text-slate-600">
                                        Projects delivered
                                    </p>
                                </div>
                            </div>

                            {/* Bottom Card */}
                            <div className="mt-4 rounded-[24px] border border-[var(--color-border)]/70 bg-[var(--color-dark)] p-4 text-white">
                                <p className="text-sm text-white/80">
                                    End-to-end fabrication and infrastructure
                                    execution with modern systems and trusted
                                    delivery.
                                </p>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>

            {/* =====================================================
                QUOTE MODAL
            ====================================================== */}
            {modalOpen && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-2 sm:p-4 backdrop-blur-sm"
                    onClick={() => setModalOpen(false)}
                >
                    <div
                        className="relative w-full max-w-2xl max-h-[90vh] overflow-hidden rounded-3xl bg-white shadow-2xl shadow-slate-950/20"
                        onClick={(e) => e.stopPropagation()}
                    >

                        {/* Modal Header */}
                        <div className="flex items-center justify-end border-b border-slate-200 bg-white px-4 py-3 sm:px-6">
                            <button
                                type="button"
                                onClick={() => setModalOpen(false)}
                                className="rounded-full border border-slate-200 bg-slate-100 p-2 text-slate-600 hover:bg-slate-200"
                                aria-label="Close modal"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {/* Modal Content */}
                        <div className="max-h-[calc(90vh-64px)] overflow-y-auto px-4 py-5 sm:px-6 sm:py-6">

                            {/* Modal Heading */}
                            <div className="mb-6 text-center">
                                <p className="text-sm uppercase tracking-[0.3em] text-slate-500">
                                    Start your project
                                </p>

                                <h2 className="mt-3 text-2xl font-semibold text-slate-900 sm:text-3xl">
                                    Request a Quote
                                </h2>

                                <p className="mt-2 text-sm text-slate-600">
                                    Tell us about your project and we’ll get
                                    back to you with a tailored solution.
                                </p>
                            </div>

                            {/* Success Message */}
                            {submitted ? (
                                <div className="rounded-3xl border border-green-200 bg-green-50 p-6 text-center text-green-800">
                                    Thank you! Your request has been received.
                                </div>
                            ) : (

                                /* Contact Form */
                                <form
                                    onSubmit={handleSubmit}
                                    className="space-y-4"
                                >

                                    {/* Name + Email */}
                                    <div className="grid gap-4 sm:grid-cols-2">

                                        {/* Name */}
                                        <label className="block">
                                            <span className="text-sm font-medium text-slate-700">
                                                Name
                                            </span>

                                            <input
                                                type="text"
                                                name="name"
                                                value={contactData.name}
                                                onChange={handleInputChange}
                                                required
                                                className="mt-2 w-full rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-3 text-[var(--color-dark)] outline-none transition focus:border-[var(--color-accent)] focus:ring-2 focus:ring-[var(--color-accent)]/20"
                                                placeholder="Your name"
                                            />
                                        </label>

                                        {/* Email */}
                                        <label className="block">
                                            <span className="text-sm font-medium text-slate-700">
                                                Email
                                            </span>

                                            <input
                                                type="email"
                                                name="email"
                                                value={contactData.email}
                                                onChange={handleInputChange}
                                                required
                                                className="mt-2 w-full rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-3 text-[var(--color-dark)] outline-none transition focus:border-[var(--color-accent)] focus:ring-2 focus:ring-[var(--color-accent)]/20"
                                                placeholder="you@example.com"
                                            />
                                        </label>
                                    </div>

                                    {/* Phone + Subject */}
                                    <div className="grid gap-4 sm:grid-cols-2">

                                        {/* Phone */}
                                        <label className="block">
                                            <span className="text-sm font-medium text-slate-700">
                                                Phone
                                            </span>

                                            <input
                                                type="tel"
                                                name="phone"
                                                value={contactData.phone}
                                                onChange={handleInputChange}
                                                className="mt-2 w-full rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-3 text-[var(--color-dark)] outline-none transition focus:border-[var(--color-accent)] focus:ring-2 focus:ring-[var(--color-accent)]/20"
                                                placeholder="+91 96508 46272"
                                            />
                                        </label>

                                        {/* Subject */}
                                        <label className="block">
                                            <span className="text-sm font-medium text-slate-700">
                                                Subject
                                            </span>

                                            <input
                                                type="text"
                                                name="subject"
                                                value={contactData.subject}
                                                onChange={handleInputChange}
                                                className="mt-2 w-full rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-3 text-[var(--color-dark)] outline-none transition focus:border-[var(--color-accent)] focus:ring-2 focus:ring-[var(--color-accent)]/20"
                                                placeholder="Project details"
                                            />
                                        </label>
                                    </div>

                                    {/* Message */}
                                    <label className="block">
                                        <span className="text-sm font-medium text-slate-700">
                                            Message
                                        </span>

                                        <textarea
                                            name="message"
                                            value={contactData.message}
                                            onChange={handleInputChange}
                                            rows={4}
                                            className="mt-2 w-full rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-3 text-[var(--color-dark)] outline-none transition focus:border-[var(--color-accent)] focus:ring-2 focus:ring-[var(--color-accent)]/20"
                                            placeholder="Tell us about your requirements"
                                        />
                                    </label>

                                    {/* Submit */}
                                    <button
                                        type="submit"
                                        className="btn-primary w-full justify-center"
                                    >
                                        Send Request
                                    </button>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </motion.section>
    );
};

export default Hero;
